import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback
} from 'react'

import {
  Modal,
  ModalOverlay,
  ModalContent
} from 'shared/components/common/modal'
import useTracker from 'src/lib/js/hooks/useTracker'
import { t } from 'src/translations/src'
import { TrackingEvents } from '../tracking/TrackingEvents'

type AlertModalContextType = {
  openAlertModal: (
    message: string,
    title?: string,
    source?: AlertModalSource
  ) => void
  closeAlertModal: () => void
}

export const AlertModalContext = createContext<AlertModalContextType>({
  openAlertModal: () => {},
  closeAlertModal: () => {}
})

export const useAlertModalContext = () => {
  return useContext(AlertModalContext)
}

/**
 * Note that this implementation may seem a little over-engineered on the surface (i.e why not hoist the state for the modal into
 * AlertModalProvider, rather than passing a function down to AlertModal and using useEvent to register the open/close functions
 * on component mount). The problem is that if you manage the state in the same component that you render props.children, then
 * props.children will re-render every time you change the modal state. As such, given that AlertModalProvider is intended to wrap
 * all page content at the App level, you might encounter infinite loops like when popping up a modal on page load to indicate an
 * error - Dismissing the modal would trigger re-render and potentially the same page load error and thus the modal would show again.
 */
export const AlertModalProvider = (props: React.PropsWithChildren<{}>) => {
  const [modalController, setModalController] = useState<AlertModalContextType>(
    { openAlertModal: () => {}, closeAlertModal: () => {} }
  )

  const registerModalController = useCallback(
    (
      openAlertModal: (message: string, title?: string) => void,
      closeAlertModal: () => void
    ) => {
      setModalController({ openAlertModal, closeAlertModal })
    },
    []
  )

  return (
    <AlertModalContext.Provider value={modalController}>
      {props.children}
      <AlertModal registerModalController={registerModalController} />
    </AlertModalContext.Provider>
  )
}

type Props = {
  registerModalController: (
    openAlertModal: (message: string, title?: string) => void,
    closeAlertModal: () => void
  ) => void
}

export enum AlertModalSource {
  QUICK_ADD_TO_CART = 'Quick add to cart',
  ITEM_MODAL = 'Item modal',
  CART = 'Cart',
  CART_QUANTITY_SELECTOR = 'Cart quantity selector',
  CHECKOUT = 'Checkout',
  PRODUCT_DISPLAY = 'Product display',
  UPSELL = 'Upsell'
}

const AlertModal = ({ registerModalController }: Props) => {
  const defaultTitle = 'alert-modal.title'
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [title, setTitle] = useState(defaultTitle)
  const { track } = useTracker()

  const openAlertModal = (
    newMessage: string,
    newTitle?: string,
    source?: AlertModalSource
  ) => {
    setMessage(newMessage)
    setTitle(newTitle || defaultTitle)
    setIsOpen(true)
    track(TrackingEvents.ALERT_MODAL_VIEWED, {
      title: t(newTitle || defaultTitle),
      message: t(newMessage),
      source
    })
  }

  const closeAlertModal = () => {
    setMessage('')
    setTitle(defaultTitle)
    setIsOpen(false)
  }

  // When the component mounts, we want to set the open/close funcs... And then unset them on dismount
  useEffect(() => {
    registerModalController(openAlertModal, closeAlertModal)

    return () => {
      registerModalController(
        () => {},
        () => {}
      )
    }
  }, [registerModalController])

  return (
    <Modal isOpen={isOpen} onClose={closeAlertModal} className='alertModal'>
      <ModalOverlay fadeIn fadeOut />
      <ModalContent fadeIn>
        <h2 className='alertTitle'>{t(title)}</h2>
        <div className='alertMessage'>{t(message)}</div>
        <button className='closeButton' onClick={closeAlertModal}>
          {t('alert-modal.button.close')}
        </button>
      </ModalContent>
    </Modal>
  )
}

export default AlertModalProvider
